import React, { useState, useEffect,useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Button, FormFeedback } from 'reactstrap';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import CustomTimePicker from '../../../../components/common/CustomTimePicker/CustomTimePicker';
import { AgendaData, EventFormData } from '../types';

interface AgendaModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (agenda: AgendaData) => void;
  isLoading?: boolean;
  agenda?: AgendaData;
  formData:EventFormData
}

const AgendaModal: React.FC<AgendaModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  isLoading = false,
  agenda,
  formData

}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [imageError, setImageError] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  // Validation schema
  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    // description: Yup.string().required('Description is required'),
    // agendaDate: Yup.string().required('Date is required'),
    // startTime: Yup.string().required('Start time is required'),
    // endTime: Yup.string().required('End time is required'),
    // hostName: Yup.string().required('Host name is required'),
  });

  // Initial values
  const initialValues: AgendaData = {
    title: agenda?.title || "",
    description: agenda?.description || "",
    agendaDate: agenda?.agendaDate || formData.eventDate,
    startTime: agenda?.startTime || "09:00",
    endTime: agenda?.endTime || "10:00",
    hostName: agenda?.hostName || "",
    hostImage: null,
    hostImageKey: agenda?.hostImageKey || "",
    hostImageUrl: agenda?.hostImageUrl ?? null,
    sortOrder: agenda?.sortOrder || 0,
    isActive: agenda?.isActive ?? true,
  };

  useEffect(() => {
    if (agenda?.agendaDate) {
      setSelectedDate(new Date(agenda.agendaDate));
    }
  }, [agenda]);

  const handleSubmit = (values: AgendaData) => {
    if (imageError) return;
    const agendaData: AgendaData = {
      ...values,
      id: agenda?.id,
      sortOrder: agenda?.sortOrder || 0,
      isActive: agenda?.isActive ?? true,
    };
    onSubmit(agendaData);
  };

  return (
    <Modal modalClassName="quick-v2-glass-modal" isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <i className="fa-solid fa-calendar-plus me-2"></i>
        {agenda ? "Edit Agenda Item" : "Add New Agenda Item"}
      </ModalHeader>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleChange,
          handleBlur,
        }) => (
          <Form>
            <ModalBody>
              <div className="row">
                {/* Title */}
                <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label for="title" className="form-label">
                      Session Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.title && !!errors.title}
                    />
                    <FormFeedback>{errors.title}</FormFeedback>
                  </FormGroup>
                </div>

                {/* Description */}
                <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label for="description" className="form-label">
                      Session Description 
                    </Label>
                    <Input
                      id="description"
                      name="description"
                      type="textarea"
                      rows={3}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.description && !!errors.description}
                    />
                    <FormFeedback>{errors.description}</FormFeedback>
                  </FormGroup>
                </div>

                {/* Date */}
                {/* <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label className="form-label">Session Date</Label>
                    <FlatPickr
                      className="form-control"
                      value={selectedDate || undefined}
                      onChange={([date]) => {
                        setSelectedDate(date);
                        setFieldValue("agendaDate", date);
                      }}
                      options={{
                        dateFormat: "Y-m-d",
                        minDate: "today",
                        time_24hr: true,
                      }}
                    />
                    {touched.agendaDate && errors.agendaDate && (
                      <div className="text-danger small mt-1">
                        {errors.agendaDate}
                      </div>
                    )}
                  </FormGroup>
                </div> */}

                {/* Time Range */}
                <div className="col-md-6 mb-3">
                  <FormGroup>
                    <Label for="startTime" className="form-label">
                      Start Time <span className="text-danger">*</span>
                    </Label>
                    <CustomTimePicker
                      value={values.startTime}
                      onChange={(val) => setFieldValue('startTime', val)}
                      placeholder="Select start time"
                    />
                    {touched.startTime && errors.startTime && (
                      <div className="text-danger small mt-1">{errors.startTime}</div>
                    )}
                  </FormGroup>
                </div>

                <div className="col-md-6 mb-3">
                  <FormGroup>
                    <Label for="endTime" className="form-label">
                      End Time
                    </Label>
                    <CustomTimePicker
                      value={values.endTime}
                      onChange={(val) => setFieldValue('endTime', val)}
                      placeholder="Select end time"
                    />
                    {touched.endTime && errors.endTime && (
                      <div className="text-danger small mt-1">{errors.endTime}</div>
                    )}
                  </FormGroup>
                </div>

                {/* Host Name */}
                {/* <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label for="hostName" className="form-label">
                      Host/Speaker Name
                    </Label>
                    <Input
                      id="hostName"
                      name="hostName"
                      type="text"
                      value={values.hostName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.hostName && !!errors.hostName}
                    />
                    <FormFeedback>{errors.hostName}</FormFeedback>
                  </FormGroup>
                </div> */}

                {/* Host Image */}
                {/* <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label for="hostImage" className="form-label">
                      Host Image (Optional)
                    </Label>

                    {previewImage && (
                      <div className="mb-2">
                        <img
                          src={previewImage}
                          alt="Preview"
                          style={{
                            width: 120,
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: 4,
                            border: '1px solid #ccc'
                          }}
                        />
                      </div>
                    )}

                    <Input
                      id="hostImage"
                      name="hostImage"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setImageError('');
                        if (!file) return;

                        if (file.size > 2 * 1024 * 1024) {
                          setImageError('Max file size is 2 MB');
                          return;
                        }

                        const url = URL.createObjectURL(file);
                        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
                        previewUrlRef.current = url;
                        setPreviewImage(url);
                        setFieldValue("hostImage", file);
                        setFieldValue("hostImageKey", file.name);
                        setFieldValue("hostImageUrl", url);
                      }}
                      invalid={!!imageError}
                    />
                    {imageError && <FormFeedback>{imageError}</FormFeedback>}
                    <small className="text-muted">
                      Max file size: 2 MB.
                    </small>
                  </FormGroup>
                </div> */}

                {/* Status */}
                {/* <div className="col-md-12 mb-3">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={values.isActive}
                        onChange={(e) =>
                          setFieldValue("isActive", e.target.checked)
                        }
                      />
                      Active Session
                    </Label>
                  </FormGroup>
                </div> */}
              </div>
            </ModalBody>

            <ModalFooter>
              <Button color="secondary" onClick={toggle} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isLoading}
                className="btn-hover"
              >
                {isLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin me-2"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-save me-2"></i>
                    {agenda ? "Update" : "Save"} Agenda
                  </>
                )}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AgendaModal;
